import React, { useEffect, useState } from 'react';
import { config } from '../config/environment';
import { useCountryStates } from '../hooks/useCountryStates';

export interface CountryOption {
  id: string;
  name: string;
  iso_code: string;
}

interface CountryStateSelectProps {
  /** Selected country ISO 3166-1 alpha-2 code (e.g. "US"). */
  country: string;
  /** Selected state/province — a subdivision code when a list exists, else free text. */
  stateProvince: string;
  onCountryChange: (iso: string) => void;
  onStateChange: (value: string) => void;
  countryError?: string;
  stateError?: string;
  /** Marks both fields with a "*" and is purely presentational. */
  required?: boolean;
  /** "dark" matches the coach app forms; "light" matches modals. */
  variant?: 'light' | 'dark';
  /** Disambiguates element ids when several instances render on one page. */
  idPrefix?: string;
  onBlur?: (field: 'country' | 'state_province') => void;
}

/**
 * Country -> state dependent selector backed by DB reference data (E1.3).
 *
 * Loads the active country list from GET /api/countries once, and the
 * subdivisions for the selected country from GET /api/countries/{iso}/states
 * whenever the country changes. When the selected country has subdivision rows
 * (e.g. the US) the state field is a dropdown; when it has none the field falls
 * back to free text (still required by the parent form). Changing the country
 * clears any previously chosen state/province.
 *
 * Reusable across client self-registration (E1.1), coach invite (E1.2), and
 * student intake (E2) so the list is never hardcoded in a component.
 */
const CountryStateSelect: React.FC<CountryStateSelectProps> = ({
  country,
  stateProvince,
  onCountryChange,
  onStateChange,
  countryError,
  stateError,
  required = false,
  variant = 'light',
  idPrefix = 'cs',
  onBlur,
}) => {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  // Subdivisions for the selected country come from the shared DB-backed hook —
  // the single implementation of the states fetch (E1.8 / CSECR-64).
  const { states, loading: statesLoading } = useCountryStates(country);

  // Load the active country list once. Non-fatal on failure: the dropdown stays
  // empty and the parent form's submit-time validation blocks an empty value.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.apiBaseUrl}/countries`);
        if (!res.ok) return;
        const data: CountryOption[] = await res.json();
        if (!cancelled) setCountries(data);
      } catch {
        /* ignore — field stays empty */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasStateList = states.length > 0;

  const labelClass = variant === 'dark'
    ? 'block text-xs font-medium text-gray-400 mb-1'
    : 'block text-sm font-medium text-gray-700 mb-1';

  const errorTextClass = variant === 'dark'
    ? 'text-red-400 text-xs mt-1'
    : 'mt-1 text-sm text-red-600';

  const fieldClass = (hasError: boolean) => {
    if (variant === 'dark') {
      return `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-dark-canvas text-gray-200 placeholder-dark-muted ${
        hasError ? 'border-red-500' : 'border-dark-border'
      }`;
    }
    return `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
      hasError ? 'border-red-300' : 'border-gray-300'
    }`;
  };

  const star = required ? ' *' : '';

  return (
    <>
      <div>
        <label htmlFor={`${idPrefix}-country`} className={labelClass}>Country{star}</label>
        <select
          id={`${idPrefix}-country`}
          name="country"
          value={country}
          onChange={(e) => {
            // Changing the country invalidates any previously picked state.
            onCountryChange(e.target.value);
            onStateChange('');
          }}
          onBlur={() => onBlur?.('country')}
          className={fieldClass(!!countryError)}
        >
          <option value="">Select a country…</option>
          {countries.map((c) => (
            <option key={c.iso_code} value={c.iso_code}>{c.name}</option>
          ))}
        </select>
        {countryError && <p className={errorTextClass}>{countryError}</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-state`} className={labelClass}>State/Province{star}</label>
        {hasStateList ? (
          <select
            id={`${idPrefix}-state`}
            name="state_province"
            value={stateProvince}
            onChange={(e) => onStateChange(e.target.value)}
            onBlur={() => onBlur?.('state_province')}
            className={fieldClass(!!stateError)}
          >
            <option value="">Select a state…</option>
            {states.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            id={`${idPrefix}-state`}
            name="state_province"
            value={stateProvince}
            onChange={(e) => onStateChange(e.target.value)}
            onBlur={() => onBlur?.('state_province')}
            className={fieldClass(!!stateError)}
            placeholder={statesLoading ? 'Loading…' : 'State / Province / Region'}
          />
        )}
        {stateError && <p className={errorTextClass}>{stateError}</p>}
      </div>
    </>
  );
};

export default CountryStateSelect;
